import { doc, DocumentData, updateDoc } from 'firebase/firestore'
import * as React from 'react'
import { Data } from 'react-firebase-hooks/firestore/dist/firestore/types'
import { usePlaidLink } from 'react-plaid-link'
import plaid from '../../assets/plaid.png'
import { firebaseAuth, firestore } from '../../config/firebase'
import { Button } from '../../shared-components/Button'
import { SecondaryButton } from '../../shared-components/SecondaryButton'
import { useScreenTracking } from '../../shared-hooks/useScreenTracking'
import { USERS_COLLECTION } from '../../shared-hooks/useUserData'
import { track } from '../../utils/analytics'
import { SignupCard } from './SignupCard'
import { REQUEST_OPTIONS, SignupStep } from './SignupFlow'

const PLAID_URL =
  'https://us-central1-infra-test-308817.cloudfunctions.net/plaid'

type PlaidProps = {
  userData: Data<DocumentData, '', ''>
  setSignupStep: (step: SignupStep) => void
}

export const Plaid = ({ userData, setSignupStep }: PlaidProps): JSX.Element => {
  useScreenTracking('PlaidView', 'View plaid screen')

  const [isPlaidSubmitting, setIsPlaidSubmitting] = React.useState(false)
  const [plaidToken, setPlaidToken] = React.useState<string | null>(null)
  const [jwtAuthToken, setJWTAuthToken] = React.useState('')
  const { open, ready } = usePlaidLink({
    token: plaidToken,
    onExit: () => {
      track('ConnectPlaidExit', 'Connect plaid exit')
      setIsPlaidSubmitting(false)
    },
    onSuccess: async (public_token) => {
      // TODO: This should maybe be in its own `useCallback` hook, but the
      //       difference should be negligable.
      track('ConnectPlaidSuccess', 'Connect plaid success')
      const headers = {
        ...REQUEST_OPTIONS.headers,
        Authorization: `Bearer ${jwtAuthToken}`,
      }
      const body = {
        stage: 'public_token',
        public_token,
      }

      try {
        await fetch(PLAID_URL, {
          ...REQUEST_OPTIONS,
          headers,
          body: JSON.stringify(body),
        })

        const userDocRef = doc(
          firestore,
          USERS_COLLECTION,
          firebaseAuth.currentUser.uid
        )
        await updateDoc(userDocRef, { status: 'active' })
        setSignupStep(SignupStep.Finished)
      } catch (error) {
        console.error(error)
      }
    },
  })

  React.useEffect(() => {
    if (ready) {
      open()
    }
  }, [ready, open])

  const openPlaid = async () => {
    setIsPlaidSubmitting(true)
    track('AttemptConnectPlaid', 'Attempt connect plaid')

    try {
      const idToken = await firebaseAuth.currentUser.getIdToken()
      setJWTAuthToken(idToken)

      const headers = {
        ...REQUEST_OPTIONS.headers,
        Authorization: `Bearer ${idToken}`,
      }
      const body = {
        stage: 'link_token',
        email: userData.email,
        phone_number: `+${userData.phone}`,
        legal_name: `${userData.first_name} ${userData.last_name}`,
      }

      const response = await fetch(PLAID_URL, {
        ...REQUEST_OPTIONS,
        headers,
        body: JSON.stringify(body),
      })
      const { link_token } = await response.json()
      setPlaidToken(link_token)
    } catch (error) {
      console.error(error)
      setIsPlaidSubmitting(false)
    }
  }

  return (
    <SignupCard
      headerComponent={
        <button
          className='appearance-none text-brand-purple font-normal float-right relative -top-6 hover-hover:hover:opacity-60 active:opacity-60'
          onClick={() => {
            track('SignupSkipPlaid', 'Sign up skip plaid')
            setSignupStep(SignupStep.Finished)
          }}
        >
          Skip for now
        </button>
      }
      iconSrc={plaid}
      heading='Enable transactions'
      subheading='Finish registration by connecting your bank account through Plaid, or debit card through Stripe. Your data is securely encrypted, and only used for transaction purposes.'
    >
      {firebaseAuth.currentUser && userData && (
        <div className='-mt-4'>
          <Button
            type='button'
            onClick={openPlaid}
            isSubmitting={isPlaidSubmitting}
          >
            Connect Bank Account
          </Button>
          <div className='mt-1.5'>
            <SecondaryButton
              type='button'
              onClick={() => setSignupStep(SignupStep.Stripe)}
              disabled={isPlaidSubmitting}
            >
              Connect Debit Card
            </SecondaryButton>
          </div>
        </div>
      )}
    </SignupCard>
  )
}
